import { matchesCurrentMoveOutRequest } from '../../utils/portvilleMoveOutRequest';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  where,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  getDoc,
  deleteField
} from 'firebase/firestore';
import { deleteObject, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../firebase';
import {
  PortvilleRoom,
  PortvilleResidency,
  PortvilleRoomFormData,
  PortvilleRoomWithResidency,
  FacilityFormData,
  RoomStatusFilter,
  PortvilleRoommate,
  PortvilleRoommateFormData
} from '../../types/portvilleRoom';
import {
  normalizePortvilleRoommates,
  ROOMMATE_SLOT_FIELDS
} from '../../utils/portvilleRoommates';

// 상수 정의
const RECONNECT_DELAY = 5000;
const MAX_RETRY_ATTEMPTS = 5;

const sanitizeRoommateFileName = (fileName: string) =>
  Array.from(fileName)
    .map((character) => {
      const code = character.charCodeAt(0);
      return character === '/' || character === '\\' || code < 32 || code === 127
        ? '_'
        : character;
    })
    .join('')
    .slice(-180);

const createAttachmentId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(36).slice(2)}`;

const serializeRoommateAttachment = (attachment: PortvilleRoommate['attachment']) => {
  if (!attachment) return undefined;

  return {
    id: attachment.id,
    name: attachment.name,
    storagePath: attachment.storagePath,
    size: attachment.size,
    contentType: attachment.contentType,
    uploadedAt: attachment.uploadedAt
      ? Timestamp.fromDate(attachment.uploadedAt)
      : Timestamp.now()
  };
};

const buildRoommateFirestoreFields = (
  roommates: PortvilleRoommate[],
  clearMissingFields: boolean
): Record<string, any> => {
  const fields: Record<string, any> = {
    roommateCount: roommates.length
  };

  ROOMMATE_SLOT_FIELDS.forEach((slotFields, index) => {
    const roommate = roommates[index];

    if (roommate) {
      fields[slotFields.name] = roommate.name.trim();
      fields[slotFields.contact] = roommate.contact.trim();
      const attachment = serializeRoommateAttachment(roommate.attachment);
      if (attachment) {
        fields[slotFields.attachment] = attachment;
      } else if (clearMissingFields) {
        fields[slotFields.attachment] = deleteField();
      }
      return;
    }

    if (clearMissingFields) {
      fields[slotFields.name] = deleteField();
      fields[slotFields.contact] = deleteField();
      fields[slotFields.attachment] = deleteField();
    }
  });

  return fields;
};

const uploadPendingRoommateFiles = async (
  residencyId: string,
  roommates: PortvilleRoommateFormData[]
): Promise<{ roommates: PortvilleRoommate[]; uploadedPaths: string[] }> => {
  const uploadedPaths: string[] = [];

  try {
    const uploadedRoommates: PortvilleRoommate[] = [];
    for (const roommate of roommates) {
      if (!roommate.pendingAttachmentFile) {
        uploadedRoommates.push({
          name: roommate.name,
          contact: roommate.contact,
          attachment: roommate.attachment
        });
        continue;
      }

      const file = roommate.pendingAttachmentFile;
      const attachmentId = createAttachmentId();
      const storagePath =
        `portville_roommate_documents/${residencyId}/${attachmentId}/` +
        sanitizeRoommateFileName(file.name);
      await uploadBytes(ref(storage, storagePath), file, { contentType: file.type });
      uploadedPaths.push(storagePath);

      uploadedRoommates.push({
        name: roommate.name,
        contact: roommate.contact,
        attachment: {
          id: attachmentId,
          name: file.name,
          storagePath,
          size: file.size,
          contentType: file.type,
          uploadedAt: new Date()
        }
      });
    }

    return { roommates: uploadedRoommates, uploadedPaths };
  } catch (error) {
    await Promise.allSettled(uploadedPaths.map((path) => deleteObject(ref(storage, path))));
    throw error;
  }
};

const getRoommateAttachmentPaths = (roommates: PortvilleRoommate[]) =>
  roommates
    .map((roommate) => roommate.attachment?.storagePath)
    .filter((path): path is string => Boolean(path));

const getFormRoommates = (formData: PortvilleRoomFormData): PortvilleRoommateFormData[] => {
  if (Array.isArray(formData.roommates)) return formData.roommates.slice(0, 3);
  if (formData.roommateInfo || formData.roommateContact) {
    return [{
      name: formData.roommateInfo || '',
      contact: formData.roommateContact || ''
    }];
  }
  return [];
};

const deleteObsoleteRoommateFiles = async (paths: string[]) => {
  if (paths.length === 0) return;

  const results = await Promise.allSettled(
    paths.map((path) => deleteObject(ref(storage, path)))
  );
  if (results.some((result) => result.status === 'rejected')) {
    console.warn('동거인 기존 첨부파일 일부 삭제 실패:', results);
    window.alert(
      '동거인 정보는 저장되었지만 기존 첨부파일 삭제에 실패했습니다. 관리자에게 문의해주세요.'
    );
  }
};

// 타입 정의
interface LoadingState {
  rooms: boolean;
  residencies: boolean;
  moveOutRequests: boolean;
}

interface ErrorState {
  type: 'network' | 'permission' | 'validation' | 'unknown';
  message: string;
  details?: string;
  retryCount: number;
}

type MoveOutRequest = {
  id: string;
  roomId: string;
  [key: string]: any;
};

type ContractCancellationRequest = {
  lotteryId: string;
  roomId: string;
  roomNumber: string;
  uid: string;
  reason?: string;
  requestedAt?: Date;
};

export const usePortvilleRooms = () => {
  // 상태 관리
  const [allRooms, setAllRooms] = useState<PortvilleRoomWithResidency[]>([]);
  const [pendingMoveOutRequests, setPendingMoveOutRequests] = useState<MoveOutRequest[]>([]);
  const [pendingContractCancellations, setPendingContractCancellations] = useState<ContractCancellationRequest[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    rooms: true,
    residencies: true,
    moveOutRequests: true
  });
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<RoomStatusFilter>('all');
  const [expiryFilter, setExpiryFilter] = useState<'all' | 'expired' | 'expiring-soon' | 'expiring-month'>('all');

  // 구독 관리
  const unsubscribeRef = useRef<Unsubscribe | null>(null);
  const residenciesUnsubscribeRef = useRef<Unsubscribe | null>(null);
  const moveOutUnsubscribeRef = useRef<Unsubscribe | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 데이터 상태
  const [roomsData, setRoomsData] = useState<PortvilleRoom[]>([]);
  const [residenciesData, setResidenciesData] = useState<PortvilleResidency[]>([]);

  // 전체 로딩 상태 계산
  const loading = useMemo(() =>
    loadingState.rooms || loadingState.residencies || loadingState.moveOutRequests,
    [loadingState]
  );

  const applyManualExtensionFields = useCallback((target: Record<string, any>, formData: PortvilleRoomFormData) => {
    if (!formData.hasManualExtension1 || !formData.originalContractEndDate) return;

    target.hasExtended = true;
    target.extensionStatus = 'contracted';
    target.originalEndDate = Timestamp.fromDate(new Date(formData.originalContractEndDate));
  }, []);

  const shouldClearManualExtensionFields = useCallback((residencyData: Record<string, any> | undefined) => {
    if (!residencyData) return true;

    const hasSecondExtensionActivity = Boolean(
      residencyData.hasSecondExtended ||
        residencyData.secondExtensionStatus === 'applied' ||
        residencyData.secondExtensionStatus === 'approved' ||
        residencyData.secondExtensionStatus === 'contracted' ||
        residencyData.secondExtensionContractSignedAt ||
        residencyData.secondOriginalEndDate
    );

    const hasRealExtensionActivity = Boolean(
      residencyData.extensionContractSignedAt ||
        residencyData.extensionAppliedAt ||
        residencyData.extensionApprovedAt ||
        residencyData.extensionRejectedAt ||
        residencyData.extensionSignatureUrl ||
        residencyData.extensionLandlordSignedContractUrl ||
        residencyData.extensionDocumentsUpdatedAt ||
        (residencyData.extensionDocuments && Object.values(residencyData.extensionDocuments).some(Boolean))
    );

    return !hasSecondExtensionActivity && !hasRealExtensionActivity;
  }, []);

  // 메모이제이션된 정렬 함수
  const sortRoomsByNumber = useCallback((rooms: PortvilleRoomWithResidency[]): PortvilleRoomWithResidency[] => {
    return [...rooms].sort((a, b) => {
      const numA = parseInt(a.roomNumber.replace(/\D/g, ''), 10) || 0;
      const numB = parseInt(b.roomNumber.replace(/\D/g, ''), 10) || 0;
      return numA - numB;
    });
  }, []);

  // 최적화된 데이터 결합 함수
  const combineRoomAndResidency = useCallback((rooms: PortvilleRoom[], residencies: PortvilleResidency[]): PortvilleRoomWithResidency[] => {
    const residencyMap = new Map(residencies.map(r => [r.id, r]));

    const combinedRooms = rooms.map(room => {
      const currentResidency = room.currentResidencyId
        ? residencyMap.get(room.currentResidencyId)
        : undefined;

      const shouldHideContractorInfo =
        room.status === 'vacant' || currentResidency?.status === 'completed';

      const combined: PortvilleRoomWithResidency = {
        id: room.id,
        roomNumber: room.roomNumber,
        roomType: room.roomType || '호실',
        status: room.status,
        createdAt: room.createdAt,
        updatedAt: room.updatedAt,
        currentResidencyId: room.currentResidencyId,
        currentResident: shouldHideContractorInfo ? undefined : room.currentResident,
        residentUid: shouldHideContractorInfo ? undefined : room.residentUid,
        facilityInfo: room.facilityInfo
      };

      if (!shouldHideContractorInfo && currentResidency && room.roomType !== '시설물') {
        Object.assign(combined, {
          currentResident: currentResidency.residentInfo,
          residentUid: currentResidency.residentUid,
          occupancyPeriod: currentResidency.occupancyPeriod,
          roommateInfo: currentResidency.roommateInfo,
          roommateContact: currentResidency.roommateContact,
          roommateCount: currentResidency.roommateCount,
          roommateAttachment: currentResidency.roommateAttachment,
          roommate2Info: currentResidency.roommate2Info,
          roommate2Contact: currentResidency.roommate2Contact,
          roommate2Attachment: currentResidency.roommate2Attachment,
          roommate3Info: currentResidency.roommate3Info,
          roommate3Contact: currentResidency.roommate3Contact,
          roommate3Attachment: currentResidency.roommate3Attachment,
          roommates: normalizePortvilleRoommates(currentResidency),
          vehicleInfo: currentResidency.vehicleInfo,
          notes: currentResidency.notes,
          signatureName: currentResidency.signatureName,
          signatureDate: currentResidency.signatureDate,
          signatureUrl: currentResidency.signatureUrl,
          contractSignedAt: currentResidency.contractSignedAt,
          moveInInspectionSignedAt: currentResidency.moveInInspectionSignedAt,
          moveInInspectionSignatureUrl: currentResidency.moveInInspectionSignatureUrl,
          moveOutInspectionSignedAt: (currentResidency as any).moveOutInspectionSignedAt,
          moveOutInspectionSignatureUrl: (currentResidency as any).moveOutInspectionSignatureUrl,
          moveOutInspectionNotes: (currentResidency as any).moveOutInspectionNotes,
          moveOutInspectionCharges: currentResidency.moveOutInspectionCharges,
          // 계약 연장 관련 필드
          hasExtended: currentResidency.hasExtended,
          extensionStatus: currentResidency.extensionStatus,
          extensionAppliedAt: currentResidency.extensionAppliedAt,
          extensionApprovedAt: currentResidency.extensionApprovedAt,
          extensionRejectedAt: currentResidency.extensionRejectedAt,
          extensionRejectionReason: currentResidency.extensionRejectionReason,
          originalEndDate: currentResidency.originalEndDate,
          extensionDocuments: currentResidency.extensionDocuments,
          extensionDocumentsUpdatedAt: currentResidency.extensionDocumentsUpdatedAt,
          // 연장계약 서명 정보
          extensionSignatureName: currentResidency.extensionSignatureName,
          extensionSignatureDate: currentResidency.extensionSignatureDate,
          extensionSignatureUrl: currentResidency.extensionSignatureUrl,
          extensionContractSignedAt: currentResidency.extensionContractSignedAt,
          extensionLandlordSignedContractUrl: currentResidency.extensionLandlordSignedContractUrl,
          extensionLandlordSignedContractUploadedAt: currentResidency.extensionLandlordSignedContractUploadedAt,
          extensionContractContent: currentResidency.extensionContractContent,
          // 2차 계약 연장 관련 필드
          hasSecondExtended: currentResidency.hasSecondExtended,
          secondExtensionStatus: currentResidency.secondExtensionStatus,
          secondExtensionAppliedAt: currentResidency.secondExtensionAppliedAt,
          secondExtensionApprovedAt: currentResidency.secondExtensionApprovedAt,
          secondExtensionRejectedAt: currentResidency.secondExtensionRejectedAt,
          secondExtensionRejectionReason: currentResidency.secondExtensionRejectionReason,
          secondOriginalEndDate: currentResidency.secondOriginalEndDate,
          secondExtensionDocuments: currentResidency.secondExtensionDocuments,
          secondExtensionDocumentsUpdatedAt: currentResidency.secondExtensionDocumentsUpdatedAt,
          // 2차 연장계약 서명 정보
          secondExtensionSignatureName: currentResidency.secondExtensionSignatureName,
          secondExtensionSignatureDate: currentResidency.secondExtensionSignatureDate,
          secondExtensionSignatureUrl: currentResidency.secondExtensionSignatureUrl,
          secondExtensionContractSignedAt: currentResidency.secondExtensionContractSignedAt,
          secondExtensionLandlordSignedContractUrl: currentResidency.secondExtensionLandlordSignedContractUrl,
          secondExtensionLandlordSignedContractUploadedAt:
            currentResidency.secondExtensionLandlordSignedContractUploadedAt,
          secondExtensionContractContent: currentResidency.secondExtensionContractContent,
          // 퇴거 관련 정보
          moveOutInfo: currentResidency.moveOutInfo
        });
      }

      return combined;
    });

    return sortRoomsByNumber(combinedRooms);
  }, [sortRoomsByNumber]);

  // 에러 분류 함수
  const classifyError = useCallback((error: any, context: string): ErrorState => {
    console.error(`${context} 실패:`, error);

    if (error.code === 'permission-denied') {
      return {
        type: 'permission',
        message: '접근 권한이 없습니다.',
        details: '관리자에게 문의하세요.',
        retryCount: 0
      };
    } else if (error.code === 'unavailable') {
      return {
        type: 'network',
        message: '네트워크 연결에 실패했습니다.',
        details: '잠시 후 다시 시도됩니다.',
        retryCount: (error.retryCount || 0) + 1
      };
    } else {
      return {
        type: 'unknown',
        message: `${context}에 실패했습니다.`,
        details: error.message,
        retryCount: 0
      };
    }
  }, []);

  // 지수 백오프를 사용한 재연결
  const scheduleReconnect = useCallback((retryCount: number = 0) => {
    if (retryCount >= MAX_RETRY_ATTEMPTS) {
      setError(prev => prev ? {
        ...prev,
        message: '최대 재시도 횟수를 초과했습니다.',
        details: '페이지를 새로고침해주세요.'
      } : null);
      return;
    }

    const delay = Math.min(RECONNECT_DELAY * Math.pow(2, retryCount), 30000);

    reconnectTimeoutRef.current = setTimeout(() => {
      console.log(`재연결 시도 중... (${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`);
      subscribeToRooms(retryCount);
    }, delay);
  }, []);

  // 연결 오류 처리
  const handleConnectionError = useCallback((error: any, context: string) => {
    setLoadingState(prev => ({
      ...prev,
      [context === 'rooms' ? 'rooms' : context === 'residencies' ? 'residencies' : 'moveOutRequests']: false
    }));
    setConnected(false);

    const errorState = classifyError(error, context);
    setError(errorState);

    if (errorState.type === 'network' && errorState.retryCount < MAX_RETRY_ATTEMPTS) {
      scheduleReconnect(errorState.retryCount);
    }
  }, [classifyError, scheduleReconnect]);

  // 실시간 데이터 구독
  const subscribeToRooms = useCallback((retryCount: number = 0) => {
    try {
      setLoadingState(prev => ({ ...prev, rooms: true, residencies: true }));
      setError(null);

      // 기존 구독들 해제
      unsubscribeRef.current?.();
      residenciesUnsubscribeRef.current?.();

      // 재연결 타이머 클리어
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // 호실 정보 구독
      const roomsQuery = query(collection(db, 'portville_rooms'));
      unsubscribeRef.current = onSnapshot(roomsQuery, (snapshot) => {
        const rooms = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            roomNumber: data.roomNumber,
            roomType: data.roomType || '호실',
            status: data.status,
            currentResidencyId: data.currentResidencyId,
            currentResident: data.currentResident,
            residentUid: data.residentUid,
            facilityInfo: data.facilityInfo,
            createdAt: data.createdAt.toDate(),
            updatedAt: data.updatedAt.toDate()
          } as PortvilleRoom;
        });

        setRoomsData(rooms);
        setLoadingState(prev => ({ ...prev, rooms: false }));
      }, (error) => handleConnectionError({ ...error, retryCount }, 'rooms'));

      // 입주 기록 구독
      const residenciesQuery = query(
        collection(db, 'portville_residencies'),
        where('status', 'in', ['contracting', 'active']),
        orderBy('createdAt', 'desc')
      );

      residenciesUnsubscribeRef.current = onSnapshot(residenciesQuery, (snapshot) => {
        const residencies = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            roomId: data.roomId,
            roomNumber: data.roomNumber,
            residentInfo: data.residentInfo,
            residentUid: data.residentUid,
            occupancyPeriod: data.occupancyPeriod ? {
              startDate: data.occupancyPeriod.startDate.toDate(),
              endDate: data.occupancyPeriod.endDate.toDate()
            } : undefined,
            roommateInfo: data.roommateInfo,
            roommateContact: data.roommateContact,
            roommateCount: data.roommateCount,
            roommateAttachment: data.roommateAttachment,
            roommate2Info: data.roommate2Info,
            roommate2Contact: data.roommate2Contact,
            roommate2Attachment: data.roommate2Attachment,
            roommate3Info: data.roommate3Info,
            roommate3Contact: data.roommate3Contact,
            roommate3Attachment: data.roommate3Attachment,
            vehicleInfo: data.vehicleInfo,
            notes: data.notes,
            signatureName: data.signatureName,
            signatureDate: data.signatureDate?.toDate(),
            signatureUrl: data.signatureUrl,
            contractSignedAt: data.contractSignedAt?.toDate(),
            moveInInspectionSignedAt: data.moveInInspectionSignedAt?.toDate(),
            moveInInspectionSignatureUrl: data.moveInInspectionSignatureUrl,
            // 퇴거점검 관련 필드
            moveOutInspectionSignedAt: data.moveOutInspectionSignedAt?.toDate(),
            moveOutInspectionSignatureUrl: data.moveOutInspectionSignatureUrl,
            moveOutInspectionNotes: data.moveOutInspectionNotes,
            moveOutInspectionCharges: data.moveOutInspectionCharges,
            moveOutMeterReadings: data.moveOutMeterReadings,
            // 계약 연장 관련 필드
            hasExtended: data.hasExtended || false,
            extensionStatus: data.extensionStatus || 'none',
            extensionAppliedAt: data.extensionAppliedAt?.toDate(),
            extensionApprovedAt: data.extensionApprovedAt?.toDate(),
            extensionRejectedAt: data.extensionRejectedAt?.toDate(),
            extensionRejectionReason: data.extensionRejectionReason,
            originalEndDate: data.originalEndDate?.toDate(),
            extensionDocuments: data.extensionDocuments,
            extensionDocumentsUpdatedAt: data.extensionDocumentsUpdatedAt?.toDate?.(),
            // 연장계약 서명 정보
            extensionSignatureName: data.extensionSignatureName,
            extensionSignatureDate: data.extensionSignatureDate?.toDate(),
            extensionSignatureUrl: data.extensionSignatureUrl,
            extensionContractSignedAt: data.extensionContractSignedAt?.toDate(),
            extensionLandlordSignedContractUrl: data.extensionLandlordSignedContractUrl,
            extensionLandlordSignedContractUploadedAt: data.extensionLandlordSignedContractUploadedAt?.toDate(),
            extensionContractContent: data.extensionContractContent,
            // 2차 계약 연장 관련 필드
            hasSecondExtended: data.hasSecondExtended || false,
            secondExtensionStatus: data.secondExtensionStatus || 'none',
            secondExtensionAppliedAt: data.secondExtensionAppliedAt?.toDate(),
            secondExtensionApprovedAt: data.secondExtensionApprovedAt?.toDate(),
            secondExtensionRejectedAt: data.secondExtensionRejectedAt?.toDate(),
            secondExtensionRejectionReason: data.secondExtensionRejectionReason,
            secondOriginalEndDate: data.secondOriginalEndDate?.toDate(),
            secondExtensionDocuments: data.secondExtensionDocuments,
            secondExtensionDocumentsUpdatedAt: data.secondExtensionDocumentsUpdatedAt?.toDate?.(),
            // 2차 연장계약 서명 정보
            secondExtensionSignatureName: data.secondExtensionSignatureName,
            secondExtensionSignatureDate: data.secondExtensionSignatureDate?.toDate(),
            secondExtensionSignatureUrl: data.secondExtensionSignatureUrl,
            secondExtensionContractSignedAt: data.secondExtensionContractSignedAt?.toDate(),
            secondExtensionLandlordSignedContractUrl: data.secondExtensionLandlordSignedContractUrl,
            secondExtensionLandlordSignedContractUploadedAt: data.secondExtensionLandlordSignedContractUploadedAt?.toDate(),
            secondExtensionContractContent: data.secondExtensionContractContent,
            // 퇴거 관련 정보
            moveOutInfo: data.moveOutInfo ? {
              originalEndDate: data.moveOutInfo.originalEndDate.toDate(),
              approvedMoveOutDate: data.moveOutInfo.approvedMoveOutDate.toDate(),
              moveOutReason: data.moveOutInfo.moveOutReason,
              approvedAt: data.moveOutInfo.approvedAt.toDate(),
              approvedBy: data.moveOutInfo.approvedBy,
              notes: data.moveOutInfo.notes
            } : undefined,
            status: data.status,
            createdAt: data.createdAt.toDate(),
            updatedAt: data.updatedAt.toDate(),
            completedAt: data.completedAt?.toDate()
          } as PortvilleResidency;
        });

        setResidenciesData(residencies);
        setLoadingState(prev => ({ ...prev, residencies: false }));
      }, (error) => handleConnectionError({ ...error, retryCount }, 'residencies'));

    } catch (error) {
      handleConnectionError(error, 'subscription setup');
    }
  }, [handleConnectionError]);

  // 퇴거 신청 데이터 구독
  useEffect(() => {
    let unsubscribe: Unsubscribe | null = null;

    try {
      setLoadingState(prev => ({ ...prev, moveOutRequests: true }));
      const moveOutRequestsRef = collection(db, 'portville_move_out_requests');
      const moveOutQuery = query(
        moveOutRequestsRef,
        where('status', '==', 'pending'),
        orderBy('appliedAt', 'desc')
      );

      unsubscribe = onSnapshot(
        moveOutQuery,
        (snapshot) => {
          const requests = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as MoveOutRequest));
          setPendingMoveOutRequests(requests);
          setLoadingState(prev => ({ ...prev, moveOutRequests: false }));
        },
        (error) => {
          console.error('퇴거신청 데이터 로딩 실패:', error);
          setPendingMoveOutRequests([]);
          setLoadingState(prev => ({ ...prev, moveOutRequests: false }));
        }
      );

      moveOutUnsubscribeRef.current = unsubscribe;
    } catch (error) {
      console.error('퇴거신청 리스너 설정 실패:', error);
      setPendingMoveOutRequests([]);
      setLoadingState(prev => ({ ...prev, moveOutRequests: false }));
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // 계약 전 포기신청 데이터 구독
  useEffect(() => {
    let unsubscribe: Unsubscribe | null = null;

    try {
      const cancellationQuery = query(
        collection(db, 'portville_lottery_history'),
        where('cancellationRequestStatus', '==', 'pending')
      );

      unsubscribe = onSnapshot(
        cancellationQuery,
        (snapshot) => {
          const requests = snapshot.docs.map((docSnap) => {
            const data = docSnap.data() as any;
            return {
              lotteryId: docSnap.id,
              roomId: data.roomId || '',
              roomNumber: data.roomNumber || '',
              uid: data.uid || '',
              reason: data.cancellationReason || '',
              requestedAt: data.cancellationRequestedAt?.toDate?.()
            } as ContractCancellationRequest;
          }).filter((request) => request.roomId);

          setPendingContractCancellations(requests);
        },
        (error) => {
          console.error('계약 포기신청 데이터 로딩 실패:', error);
          setPendingContractCancellations([]);
        }
      );

    } catch (error) {
      console.error('계약 포기신청 리스너 설정 실패:', error);
      setPendingContractCancellations([]);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // 데이터 조합 및 상태 업데이트
  useEffect(() => {
    if (!loadingState.rooms && !loadingState.residencies) {
      const combinedData = combineRoomAndResidency(roomsData, residenciesData);
      setAllRooms(combinedData);
      setConnected(true);

      // 성공 시 에러 상태 초기화
      if (error?.type === 'network') {
        setError(null);
      }
    }
  }, [roomsData, residenciesData, loadingState, combineRoomAndResidency, error]);

  // 퇴거신청 상태 포함한 호실 데이터
  const roomsWithMoveOutStatus = useMemo(() => {
    if (loadingState.moveOutRequests) {
      return allRooms;
    }

    const residencyMap = new Map(residenciesData.map(residency => [residency.id, residency]));
    const roomsWithMoveOut = allRooms.map(room => {
      const residency = room.currentResidencyId ? residencyMap.get(room.currentResidencyId) : undefined;
      const moveOutRequest = pendingMoveOutRequests.find(
        request => {
          return !!residency && ['active', 'contracting'].includes(residency.status) &&
            ['occupied', 'contracting'].includes(room.status) && matchesCurrentMoveOutRequest(request as any, {
              roomId: room.id, residencyId: room.currentResidencyId,
              residentUid: residency.residentUid, createdAt: residency.createdAt
            });
        }
      );
      const contractCancellation = pendingContractCancellations.find(
        request => request.roomId === room.id
      );
      return {
        ...room,
        hasPendingMoveOut: !!moveOutRequest,
        moveOutRequest: moveOutRequest || null,
        pendingContractCancellation: contractCancellation || undefined
      };
    });

    return sortRoomsByNumber(roomsWithMoveOut);
  }, [allRooms, residenciesData, pendingMoveOutRequests, pendingContractCancellations, loadingState.moveOutRequests, sortRoomsByNumber]);

  // 필터링된 호실 목록 (검색 로직 최적화)
  const rooms = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();

    const filtered = roomsWithMoveOutStatus.filter((room) => {
      // 검색 필터 (성능 최적화: 조건문 순서 개선)
      if (searchTerm) {
        const searchFields = [
          room.roomNumber,
          room.currentResident?.name,
          room.currentResident?.phoneNumber,
          room.currentResident?.organization,
          ...(room.roommates || []).flatMap((roommate) => [
            roommate.name,
            roommate.contact
          ]),
          room.facilityInfo?.facilityName,
          room.facilityInfo?.facilityType,
          room.facilityInfo?.responsiblePerson?.name
        ].filter(Boolean);

        const matches = searchFields.some(field =>
          field?.toLowerCase().includes(searchLower)
        );

        if (!matches) return false;
      }

      // 상태 필터링
      if (statusFilter !== 'all') {
        const isFacility = room.status === 'facility' || room.roomType === '시설물';

        // 시설물 필터: 시설물만 노출
        if (statusFilter === 'facility') {
          return isFacility;
        }

        // 시설물은 '전체' 및 '시설물'에서만 노출
        if (isFacility) return false;

        if (statusFilter === 'moveout-pending') {
          if (!(room as any).hasPendingMoveOut) return false;
        } else if (statusFilter === 'contract-cancellation-pending') {
          if (!room.pendingContractCancellation) return false;
        } else if (statusFilter === 'extension-pending') {
          if (room.extensionStatus !== 'applied' && room.secondExtensionStatus !== 'applied') return false;
        } else if (room.status !== statusFilter) {
          return false;
        }
      }

      // 만료일 필터링
      if (expiryFilter !== 'all' && room.roomType !== '시설물') {
        if (!room.occupancyPeriod?.endDate) {
          return false;
        }

        const today = new Date();
        const endDate = room.occupancyPeriod.endDate;
        const diffDays = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        switch (expiryFilter) {
          case 'expired':
            if (diffDays >= 0) return false;
            break;
          case 'expiring-soon':
            if (diffDays < 0 || diffDays > 7) return false;
            break;
          case 'expiring-month':
            if (diffDays < 0 || diffDays > 30) return false;
            break;
        }
      }

      return true;
    });

    return filtered;
  }, [roomsWithMoveOutStatus, searchTerm, statusFilter, expiryFilter]);

  // CRUD 함수들은 기존과 동일하게 유지하되 에러 처리만 개선
  const addRoom = async (formData: PortvilleRoomFormData): Promise<boolean> => {
    try {
      const now = Timestamp.now();

      const newRoom = {
        roomNumber: formData.roomNumber,
        roomType: '호실',
        status: formData.status,
        createdAt: now,
        updatedAt: now.toDate()
      };

      const roomRef = await addDoc(collection(db, 'portville_rooms'), newRoom);

      if ((formData.status === 'occupied' || formData.status === 'contracting') && formData.residentName) {
        const formRoommates = getFormRoommates(formData);
        const roommatesWithoutPendingFiles: PortvilleRoommate[] = formRoommates.map((roommate) => ({
          name: roommate.name,
          contact: roommate.contact,
          attachment: roommate.attachment
        }));
        const residencyData: any = {
          roomId: roomRef.id,
          roomNumber: formData.roomNumber,
          residentInfo: {
            name: formData.residentName,
            phoneNumber: formData.residentPhoneNumber || '',
            organization: formData.residentOrganization || ''
          },
          status: formData.status === 'occupied' ? 'active' : 'contracting',
          createdAt: now,
          updatedAt: now,
          ...buildRoommateFirestoreFields(roommatesWithoutPendingFiles, false)
        };

        if (formData.residentUid) residencyData.residentUid = formData.residentUid;
        if (formData.vehicleInfo) residencyData.vehicleInfo = formData.vehicleInfo;
        if (formData.notes) residencyData.notes = formData.notes;

        if (formData.startDate && formData.endDate) {
          residencyData.occupancyPeriod = {
            startDate: Timestamp.fromDate(new Date(formData.startDate)),
            endDate: Timestamp.fromDate(new Date(formData.endDate))
          };
        }

        applyManualExtensionFields(residencyData, formData);

        const residencyRef = await addDoc(collection(db, 'portville_residencies'), residencyData);

        await updateDoc(roomRef, {
          currentResidencyId: residencyRef.id,
          updatedAt: now
        });

        if (formRoommates.some((roommate) => roommate.pendingAttachmentFile)) {
          try {
            const uploaded = await uploadPendingRoommateFiles(residencyRef.id, formRoommates);
            try {
              await updateDoc(residencyRef, {
                ...buildRoommateFirestoreFields(uploaded.roommates, true),
                updatedAt: Timestamp.now()
              });
            } catch (metadataError) {
              await Promise.allSettled(
                uploaded.uploadedPaths.map((path) => deleteObject(ref(storage, path)))
              );
              throw metadataError;
            }
          } catch (attachmentError) {
            console.error('신규 동거인 첨부파일 업로드 실패:', attachmentError);
            window.alert(
              '호실 정보는 저장되었지만 동거인 첨부파일 업로드에 실패했습니다. 호실 수정에서 다시 업로드해주세요.'
            );
          }
        }
      }

      return true;
    } catch (error) {
      const errorState = classifyError(error, '호실 추가');
      setError(errorState);
      return false;
    }
  };

  const createFacility = async (facilityData: FacilityFormData): Promise<string> => {
    try {
      const now = Timestamp.now();

      const newFacility = {
        roomNumber: facilityData.facilityCode,
        roomType: '시설물',
        status: 'facility',
        facilityInfo: {
          facilityType: facilityData.facilityType,
          facilityName: facilityData.facilityName,
          responsiblePerson: facilityData.responsiblePerson
        },
        createdAt: now,
        updatedAt: now.toDate()
      };

      const facilityRef = await addDoc(collection(db, 'portville_rooms'), newFacility);
      return facilityRef.id;
    } catch (error) {
      const errorState = classifyError(error, '시설물 생성');
      setError(errorState);
      throw error;
    }
  };

  const updateRoom = async (roomId: string, formData: PortvilleRoomFormData): Promise<boolean> => {
    try {
      const now = Timestamp.now();
      const roomRef = doc(db, 'portville_rooms', roomId);

      const roomDoc = await getDoc(roomRef);
      if (!roomDoc.exists()) {
        throw new Error('호실을 찾을 수 없습니다.');
      }

      const currentRoom = roomDoc.data() as PortvilleRoom;

      const roomUpdateData: any = {
        roomNumber: formData.roomNumber,
        roomType: currentRoom.roomType || '호실',
        status: formData.status,
        updatedAt: now
      };

      // 입주자 정보 처리 로직은 기존과 동일
      if ((formData.status === 'occupied' || formData.status === 'contracting') && formData.residentName) {
        if (currentRoom.currentResidencyId) {
          const residencyRef = doc(db, 'portville_residencies', currentRoom.currentResidencyId);
          const residencyDoc = await getDoc(residencyRef);
          const currentResidencyData = residencyDoc.exists() ? (residencyDoc.data() as Record<string, any>) : undefined;
          const currentRoommates = normalizePortvilleRoommates(currentResidencyData);
          const formRoommates = getFormRoommates(formData);
          const residencyUpdateData: any = {
            roomId: roomId,
            roomNumber: formData.roomNumber,
            residentInfo: {
              name: formData.residentName,
              phoneNumber: formData.residentPhoneNumber || '',
              organization: formData.residentOrganization || ''
            },
            status: formData.status === 'occupied' ? 'active' : 'contracting',
            updatedAt: now
          };

          // Firestore는 undefined 값을 허용하지 않으므로, 선택/입력되지 않은 값은 필드 제거 처리
          if (formData.residentUid) {
            residencyUpdateData.residentUid = formData.residentUid;
          } else {
            residencyUpdateData.residentUid = deleteField();
          }

          residencyUpdateData.vehicleInfo = formData.vehicleInfo ? formData.vehicleInfo : deleteField();
          residencyUpdateData.notes = formData.notes ? formData.notes : deleteField();

          // 입주중으로 설정하는 경우, 별도 날인/점검 없이 생성된 계약도 조회할 수 있게 계약 완료 시각을 보장
          if (formData.status === 'occupied') {
            residencyUpdateData.contractSignedAt = now;
          }

          if (formData.startDate && formData.endDate) {
            residencyUpdateData.occupancyPeriod = {
              startDate: Timestamp.fromDate(new Date(formData.startDate)),
              endDate: Timestamp.fromDate(new Date(formData.endDate))
            };
          }

          if (formData.hasManualExtension1) {
            applyManualExtensionFields(residencyUpdateData, formData);
          } else if (shouldClearManualExtensionFields(currentResidencyData)) {
            residencyUpdateData.hasExtended = deleteField();
            residencyUpdateData.extensionStatus = deleteField();
            residencyUpdateData.originalEndDate = deleteField();
          }

          const uploaded = await uploadPendingRoommateFiles(
            currentRoom.currentResidencyId,
            formRoommates
          );
          Object.assign(
            residencyUpdateData,
            buildRoommateFirestoreFields(uploaded.roommates, true)
          );

          try {
            await updateDoc(residencyRef, residencyUpdateData);
          } catch (updateError) {
            await Promise.allSettled(
              uploaded.uploadedPaths.map((path) => deleteObject(ref(storage, path)))
            );
            throw updateError;
          }

          const nextAttachmentPaths = new Set(getRoommateAttachmentPaths(uploaded.roommates));
          const obsoletePaths = getRoommateAttachmentPaths(currentRoommates)
            .filter((path) => !nextAttachmentPaths.has(path));
          await deleteObsoleteRoommateFiles(obsoletePaths);
        } else {
          // 새로운 입주 기록 생성 로직
          const formRoommates = getFormRoommates(formData);
          const roommatesWithoutPendingFiles: PortvilleRoommate[] = formRoommates.map((roommate) => ({
            name: roommate.name,
            contact: roommate.contact,
            attachment: roommate.attachment
          }));
          const residencyData: any = {
            roomId: roomId,
            roomNumber: formData.roomNumber,
            residentInfo: {
              name: formData.residentName,
              phoneNumber: formData.residentPhoneNumber || '',
              organization: formData.residentOrganization || ''
            },
            status: formData.status === 'occupied' ? 'active' : 'contracting',
            createdAt: now,
            updatedAt: now,
            ...buildRoommateFirestoreFields(roommatesWithoutPendingFiles, false)
          };

          if (formData.residentUid) residencyData.residentUid = formData.residentUid;
          if (formData.vehicleInfo) residencyData.vehicleInfo = formData.vehicleInfo;
          if (formData.notes) residencyData.notes = formData.notes;

          if (formData.status === 'occupied') {
            residencyData.contractSignedAt = now;
          }

          if (formData.startDate && formData.endDate) {
            residencyData.occupancyPeriod = {
              startDate: Timestamp.fromDate(new Date(formData.startDate)),
              endDate: Timestamp.fromDate(new Date(formData.endDate))
            };
          }

          applyManualExtensionFields(residencyData, formData);

          const residencyRef = await addDoc(collection(db, 'portville_residencies'), residencyData);
          roomUpdateData.currentResidencyId = residencyRef.id;

          if (formRoommates.some((roommate) => roommate.pendingAttachmentFile)) {
            try {
              const uploaded = await uploadPendingRoommateFiles(residencyRef.id, formRoommates);
              try {
                await updateDoc(residencyRef, {
                  ...buildRoommateFirestoreFields(uploaded.roommates, true),
                  updatedAt: Timestamp.now()
                });
              } catch (metadataError) {
                await Promise.allSettled(
                  uploaded.uploadedPaths.map((path) => deleteObject(ref(storage, path)))
                );
                throw metadataError;
              }
            } catch (attachmentError) {
              console.error('신규 동거인 첨부파일 업로드 실패:', attachmentError);
              window.alert(
                '호실 정보는 저장되었지만 동거인 첨부파일 업로드에 실패했습니다. 호실 수정에서 다시 업로드해주세요.'
              );
            }
          }
        }
      } else {
        if (currentRoom.currentResidencyId) {
          const residencyRef = doc(db, 'portville_residencies', currentRoom.currentResidencyId);
          await updateDoc(residencyRef, {
            status: 'completed',
            completedAt: now,
            updatedAt: now
          });

          roomUpdateData.currentResidencyId = deleteField();
        }
      }

      await updateDoc(roomRef, roomUpdateData);
      return true;
    } catch (error) {
      const errorState = classifyError(error, '호실 수정');
      setError(errorState);
      return false;
    }
  };

  const deleteRoom = async (roomId: string): Promise<boolean> => {
    try {
      const residenciesQuery = query(
        collection(db, 'portville_residencies'),
        where('roomId', '==', roomId),
        where('status', 'in', ['contracting', 'active'])
      );

      const residenciesSnapshot = await getDocs(residenciesQuery);
      const now = Timestamp.now();

      for (const residencyDoc of residenciesSnapshot.docs) {
        await updateDoc(residencyDoc.ref, {
          status: 'terminated',
          completedAt: now,
          updatedAt: now
        });
      }

      await deleteDoc(doc(db, 'portville_rooms', roomId));
      return true;
    } catch (error) {
      const errorState = classifyError(error, '호실 삭제');
      setError(errorState);
      return false;
    }
  };

  // 초기 구독 설정
  useEffect(() => {
    subscribeToRooms();

    return () => {
      unsubscribeRef.current?.();
      residenciesUnsubscribeRef.current?.();
      moveOutUnsubscribeRef.current?.();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [subscribeToRooms]);

  // 강제 새로고침 함수
  const refreshData = useCallback(() => {
    console.log('포트빌 호실 데이터 강제 새로고침...');
    subscribeToRooms();
  }, [subscribeToRooms]);

  return {
    rooms,
    allRooms: roomsWithMoveOutStatus,
    pendingMoveOutRequests,
    loading,
    connected,
    error: error?.message || null,
    errorDetails: error?.details,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    expiryFilter,
    setExpiryFilter,
    addRoom,
    createFacility,
    updateRoom,
    deleteRoom,
    refreshData
  };
};
